import type { SearchResult } from '@powerful/data';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { BusinessCard, type CardBusiness } from './business-card';

/**
 * Lista paginada de resultados (C3/C8).
 *
 * «Mostrar mais» é o caminho principal: um botão real, previsível para teclado e leitor de ecrã.
 * O carregamento automático por `IntersectionObserver` é opcional e SÓ existe sem
 * `prefers-reduced-motion` — conteúdo a surgir sozinho ao rolar é movimento não pedido.
 *
 * Ao carregar a página seguinte o foco passa para o primeiro card novo (quem estava no botão não
 * volta ao topo nem fica num botão que sumiu) e a região viva anuncia quantos estão carregados.
 * `total` é `null` na API: anuncia-se o CARREGADO, nunca um total estimado.
 */
export interface ResultsListProps {
  results?: readonly SearchResult[];
  /** Snapshot offline público: sem ranking, sem distância. */
  offline?: readonly CardBusiness[];
  hasOrigin: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  onExplain?: (result: SearchResult) => void;
  /** Rótulo da lista (ex.: «Resultados»). */
  label: string;
}

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function ResultsList({ results, offline, hasOrigin, hasMore, isFetchingMore, onLoadMore, onExplain, label }: ResultsListProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const focusFrom = useRef<number | null>(null);
  const count = results?.length ?? offline?.length ?? 0;
  const loadMoreRef = useRef(onLoadMore);
  loadMoreRef.current = onLoadMore;

  // Foco no primeiro card da nova página, depois de ela chegar ao DOM.
  useEffect(() => {
    const from = focusFrom.current;
    if (from === null || count <= from) return;
    focusFrom.current = null;
    listRef.current?.querySelectorAll<HTMLAnchorElement>('a.pc-stretch')[from]?.focus();
  }, [count]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || isFetchingMore || typeof IntersectionObserver === 'undefined' || reducedMotion()) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMoreRef.current();
    }, { rootMargin: '400px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, isFetchingMore]);

  const loadMore = () => {
    // Um segundo toque durante o pedido não duplica a página (o botão continua focável e anunciado).
    if (isFetchingMore) return;
    focusFrom.current = count;
    onLoadMore();
  };

  return (
    <div className="pc-sec gap-5">
      <ul ref={listRef} className="pc-feed" aria-label={label} data-testid="results-list">
        {results
          ? results.map((r, i) => (
              <li key={r.business.id} className={i >= 20 ? 'pc-enter' : undefined}>
                <BusinessCard business={r.business} ranking={r.ranking} hasOrigin={hasOrigin} onExplain={onExplain ? () => onExplain(r) : undefined} />
              </li>
            ))
          : offline?.map((b) => (
              <li key={b.id}>
                <BusinessCard business={b} hasOrigin={false} />
              </li>
            ))}
      </ul>
      <p className="pc-vh" role="status" aria-live="polite">{t('discovery.results.loaded', { count })}</p>
      {hasMore ? (
        <>
          <div ref={sentinel} aria-hidden="true" />
          <button className="btn btn-ghost pc-more" type="button" onClick={loadMore} aria-busy={isFetchingMore || undefined}>
            {isFetchingMore ? <span className="pc-spin" aria-hidden="true" /> : null}
            {isFetchingMore ? t('discovery.results.loadingMore') : t('discovery.results.more')}
          </button>
        </>
      ) : null}
    </div>
  );
}
